import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import ConfirmationModal from "../../../../../../parts/modals/ConfirmationModal";
import SvgIcon from "../../../../../../parts/SvgIcon";
import { rowsUnder } from "../../../../../../utils/collapse-drag-controller";

/*
 * A workout-day section separator. Renders the section title; in edit mode it
 * can be renamed inline and deleted (with confirmation). Deleting a section
 * never removes exercises — the exercises below simply rejoin the section above.
 */
const SectionSeparator = ({
  section,
  athleteId,
  reqSource = "calendar",
  // The organization owner's id — set when the acting coach is a subcoach
  // editing a program template, so the API scopes the section lookup to the
  // template's real owner instead of the subcoach's own (empty) sections.
  organizationCoach,
  editable = true,
  dragHandleClass = "",
  // Read-only divider, but allow renaming the section in place (day view).
  nameEditable = false,
  // Hide the "Section name:" label while renaming (week view is tight on space).
  showNameLabel = true,
  // The section was just created (its id matches the pending server-side
  // marker) — mount straight into rename mode instead of requiring a
  // double-click, and scroll it into view.
  autoEdit = false,
  // Show the second handle that drags the header together with its exercises
  // (calendar edit modes; the plain grabber still moves the header alone).
  groupDrag = false,
}) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const [editing, setEditing] = useState(autoEdit);
  const [name, setName] = useState(section.name || "");
  const [showDelete, setShowDelete] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const query = `?proxyAthlete=${athleteId}&reqSource=${reqSource}${
    organizationCoach ? `&organizationCoach=${organizationCoach}` : ""
  }`;

  // Focus the input without letting the browser scroll the nearest
  // scrollable ancestor (native autoFocus/`.focus()` scroll-into-view jumps
  // to whichever day the week grid's `align-items: stretch` makes tallest).
  useEffect(() => {
    if (editing) inputRef.current?.focus({ preventScroll: true });
  }, [editing]);

  // Bring a freshly created section into view. This is a deliberate,
  // targeted scroll on this exact element (not the implicit browser
  // autoFocus-scroll the effect above avoids), so it doesn't reintroduce
  // the week-grid jump-to-the-wrong-day quirk.
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount, keyed off the initial autoEdit value only.
  useEffect(() => {
    if (autoEdit) {
      containerRef.current?.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    }
  }, []);

  const saveName = () => {
    setEditing(false);
    const trimmed = (name || "").trim();
    if (trimmed === (section.name || "").trim()) return;
    submit(`/actions/exercise-section/update/${section._id}${query}`, {
      method: "POST",
      body: { name: trimmed },
    });
  };

  const remove = () => {
    setShowDelete(false);
    submit(`/actions/exercise-section/remove/${section._id}${query}`, {
      method: "POST",
      body: {},
    });
  };

  const startEditing = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditing(true);
  };

  if (!editable) {
    return (
      <div
        className="section-separator section-separator--readonly"
        data-section-id={section._id}
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
      >
        {nameEditable && editing && showNameLabel && (
          <span className="section-separator__badge">
            {t("athlete-portal:exerciseEditor.sectionName")}
          </span>
        )}
        {nameEditable && editing ? (
          <>
            <input
              className="section-separator__input"
              ref={inputRef}
              value={name}
              onFocus={(e) => e.target.select()}
              onChange={(e) => setName(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => e.key === "Enter" && saveName()}
            />
            <a
              role="button"
              href="#_"
              className="section-separator__save"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                saveName();
              }}
              title={t("athlete-portal:exerciseEditor.saveSection")}
            >
              <SvgIcon src="/img/new-icons/chip-success.svg" noFilterOnLight />
            </a>
            <a
              role="button"
              href="#_"
              className="section-separator__delete"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowDelete(true);
              }}
              title={t("athlete-portal:exerciseEditor.deleteSection")}
            >
              <img src="/img/new-icons/red-trash.svg" alt="remove" />
            </a>
          </>
        ) : (
          <span
            className={`section-separator__title${
              nameEditable ? " section-separator__title--editable" : ""
            }`}
            role={nameEditable ? "button" : undefined}
            tabIndex={nameEditable ? 0 : undefined}
            onClick={nameEditable ? startEditing : undefined}
            onKeyDown={
              nameEditable
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") startEditing(e);
                  }
                : undefined
            }
          >
            {section.name ||
              (nameEditable
                ? t("athlete-portal:exerciseEditor.newSection")
                : "")}
          </span>
        )}
        <ConfirmationModal
          isOpen={showDelete}
          icon="delete"
          title={t("athlete-portal:exerciseEditor.deleteSectionTitle")}
          contentText={t("athlete-portal:exerciseEditor.deleteSectionConfirm")}
          cancelButtonText={t("common:cancel")}
          cancelButtonAction={() => setShowDelete(false)}
          confirmButtonText={t("athlete-portal:exerciseEditor.deleteSection")}
          confirmButtonAction={remove}
        />
      </div>
    );
  }

  return (
    <div
      className="section-separator"
      data-section-id={section._id}
      data-order={section.order}
      ref={containerRef}
      onClick={(e) => e.stopPropagation()}
    >
      {dragHandleClass ? (
        <img
          src="/img/new-icons/drag-outline.svg"
          className={dragHandleClass}
          title={t("athlete-portal:exerciseEditor.dragMoveSectionOnly")}
          alt={t("athlete-portal:exerciseEditor.dragMoveSectionOnly")}
        />
      ) : null}
      {groupDrag && !editing && (
        <button
          type="button"
          className="section-separator__group-handle"
          aria-label={t(
            "athlete-portal:exerciseEditor.moveSectionWithExercises"
          )}
          // The row count lives in the list around this header, so read it on hover.
          onPointerEnter={(e) => {
            const header = e.currentTarget.closest(".section-separator");
            e.currentTarget.title = t(
              "athlete-portal:exerciseEditor.dragMoveSectionWithExercises",
              { count: rowsUnder(header).length }
            );
          }}
        >
          <SvgIcon src="/img/new-icons/menu.svg" />
        </button>
      )}
      {editing && (
        <span className="section-separator__badge">
          {t("athlete-portal:exerciseEditor.sectionName")}
        </span>
      )}
      {editing ? (
        <input
          className="section-separator__input"
          ref={inputRef}
          value={name}
          onFocus={(e) => e.target.select()}
          onChange={(e) => setName(e.target.value)}
          onBlur={saveName}
          onKeyDown={(e) => e.key === "Enter" && saveName()}
        />
      ) : (
        <span
          className="section-separator__title"
          // preventDefault (via startEditing) stops the browser's native
          // double-click "select the word" action, which otherwise races
          // against the freshly focused input and is why a plain click used
          // to be needed afterwards just to place the cursor.
          onDoubleClick={startEditing}
        >
          {section.name || t("athlete-portal:exerciseEditor.newSection")}
        </span>
      )}
      <div className="section-separator__actions">
        {editing ? (
          <a
            role="button"
            href="#_"
            className="section-separator__save"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              saveName();
            }}
            title={t("athlete-portal:exerciseEditor.saveSection")}
          >
            <SvgIcon src="/img/new-icons/chip-success.svg" noFilterOnLight />
          </a>
        ) : (
          <a
            role="button"
            href="#_"
            onMouseDown={startEditing}
            title={t("athlete-portal:exerciseEditor.renameSection")}
          >
            <SvgIcon src="/img/new-icons/edit-pencil.svg" />
          </a>
        )}
        <a
          role="button"
          href="#_"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowDelete(true);
          }}
          title={t("athlete-portal:exerciseEditor.deleteSection")}
        >
          <img src="/img/new-icons/red-trash.svg" alt="remove" />
        </a>
      </div>
      <ConfirmationModal
        isOpen={showDelete}
        icon="delete"
        title={t("athlete-portal:exerciseEditor.deleteSectionTitle")}
        contentText={t("athlete-portal:exerciseEditor.deleteSectionConfirm")}
        cancelButtonText={t("common:cancel")}
        cancelButtonAction={() => setShowDelete(false)}
        confirmButtonText={t("athlete-portal:exerciseEditor.deleteSection")}
        confirmButtonAction={remove}
      />
    </div>
  );
};

export default SectionSeparator;
